// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Organico e PIN"
// Centro assistenza → guida per amministratori (HACCP).
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette dal codice.
//
// ATTENZIONE, ERRORE GIA' COMMESSO: "monitoratore" e "verificatore" NON si
// impostano qui. Sono calcolati al login dallo scadenziario (vedi guida #4 e
// #7). In questa schermata l'unico interruttore di ruolo e' "Amministratore".
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Schermata ...... App.js, GestioneOrganico — elenco con ricerca
//                    ("Cerca per nome o cognome..."), pulsante "Nuovo
//                    operatore"; contrassegni ruolo: Master (viola),
//                    Admin (blu), "Disattivato" (rosso).
//   Form ........... Nome, Cognome, "PIN *" (nuovo) / "Nuovo PIN (lascia
//                    vuoto per non modificare)" (modifica). validaPin:
//                    almeno 5 caratteri, una lettera e un numero.
//                    Checkbox "Amministratore" — sottotitolo "Accesso alla
//                    gestione (organico, oggetti, scadenziario,
//                    impostazioni)". Interruttore "Operatore attivo /
//                    Operatore disattivato" (solo in modifica).
//   Firma .......... card "Firma operatore", upload immagine (jpg/png/gif,
//                    max 2 MB); su un nuovo operatore: "Salva prima
//                    l'operatore per poter caricare la firma". Compare
//                    accanto alle righe nel Registro monitoraggi stampato.
//   Login .......... l'operatore entra col PIN sull'indirizzo dell'azienda;
//                    il PIN identifica la persona DENTRO l'azienda (bcrypt,
//                    primo match vince) → mai due persone con lo stesso PIN.
//                    attivo=false esclude dal login. Tentativi sbagliati
//                    ripetuti bloccano temporaneamente la coppia
//                    (ip, azienda) — rate limiter, si sblocca da solo.
//   Server ......... dal 19/08/2026: un admin opera solo sulla propria
//                    azienda, non puo' spostare operatori altrove ne'
//                    concedere il grado master (riservato ai master).
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Callout, Passo,
  MockFrame, Schermo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'o0', label: 'Le persone dietro le firme' },
  { id: 'o1', label: '1. Inserire una persona' },
  { id: 'o2', label: '2. Il PIN' },
  { id: 'o3', label: '3. La firma grafica' },
  { id: 'o4', label: '4. Ruoli veri e ruoli derivati' },
  { id: 'o5', label: '5. Quando una persona lascia' },
  { id: 'o6', label: 'Le trappole' },
  { id: 'o7', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const ChipFinto = ({ children, tono = 'neutro' }) => {
  const c = {
    viola:  { fg: LT.viola, bg: LT.violaSoft },
    blu:    { fg: LT.blu,   bg: LT.bluSoft },
    rosso:  { fg: LT.rosso, bg: LT.rossoSoft },
    neutro: { fg: LT.n700,  bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, background: c.bg, color: c.fg,
      fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const CampoFinto = ({ etichetta, valore, segnaposto }) => (
  <div style={{ flex: 1, minWidth: 140 }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '8px 10px',
      background: LT.n0, fontSize: 13, color: valore ? LT.n900 : LT.n400,
    }}>{valore || segnaposto || '—'}</div>
  </div>
);

const InterruttoreFinto = ({ acceso, titolo, sotto, tonoAcceso = 'blu' }) => {
  const bg = acceso ? (tonoAcceso === 'verde' ? LT.verdeSoft : LT.bluSoft) : LT.n0;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
      borderRadius: LT.r.md, border: `1px solid ${LT.n150}`, background: bg,
      marginBottom: 8,
    }}>
      <span style={{
        width: 15, height: 15, borderRadius: 4, flexShrink: 0,
        border: `1.5px solid ${acceso ? LT.blu : LT.n400}`,
        background: acceso ? LT.blu : 'transparent',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {acceso && <Ico d={I.check} size={10} stroke="#fff" sw={2.5} />}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: LT.n900 }}>{titolo}</div>
        <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>{sotto}</div>
      </div>
    </div>
  );
};

export default function Organico() {
  return (
    <GuidaLayout
      titolo="Organico e PIN"
      sottotitolo="Le persone dietro le firme: inserirle, dare a ciascuna il proprio PIN, caricare la firma che finisce sulle stampe, e gestire bene chi arriva e chi se ne va."
      durata="~ 10 minuti"
      destinatario="Per amministratori"
      sezioni={SEZIONI}
    >

      {/* o0 */}
      <H id="o0">Le persone dietro le firme</H>

      <P>
        Ogni registrazione, ogni verifica, ogni correzione in LabThree porta un nome. La
        sezione <Code>Organico</Code> è dove quei nomi nascono: qui si inseriscono le
        persone, si dà a ciascuna il PIN con cui entra, e si carica la firma grafica che
        comparirà sul registro stampato. È una schermata che si usa poco — quando qualcuno
        arriva, quando qualcuno va via — ma da come è tenuta dipende quanto sono
        difendibili tutte le firme del sistema.
      </P>

      {/* o1 */}
      <H id="o1" cat="formazione">1. Inserire una persona</H>

      <MockFrame
        url="app.labthree.it — Organico · Nuovo"
        markers={[
          { n: 1, top: '49.1%', left: '81%', titolo: 'Il PIN, con le sue regole',
            testo: 'Almeno 5 caratteri, con almeno una lettera e un numero. È la chiave personale di questa persona: non va scelto banale e non va condiviso.' },
          { n: 2, top: '63.8%', left: '80%', titolo: 'Amministratore',
            testo: 'Un solo interruttore di ruolo: apre la sezione di gestione. Tutto il resto — chi monitora, chi verifica — non si decide qui ma nello scadenziario.' },
        ]}
      >
        <Schermo>
          <div style={{
            background: LT.n0, border: `1px solid ${LT.n150}`,
            borderRadius: LT.r.lg, padding: 16, maxWidth: 460, margin: '0 auto',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900, marginBottom: 12 }}>
              Nuovo operatore
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <CampoFinto etichetta="Nome" valore="Anna" />
              <CampoFinto etichetta="Cognome" valore="Bianchi" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <CampoFinto etichetta="PIN *" segnaposto="Almeno 5 caratteri, una lettera e un numero" />
            </div>
            <InterruttoreFinto
              acceso={false}
              titolo="Amministratore"
              sotto="Accesso alla gestione (organico, oggetti, scadenziario, impostazioni)"
            />
            <div style={{
              marginTop: 12, padding: '10px 14px', borderRadius: LT.r.md,
              background: LT.verde, color: '#fff', textAlign: 'center',
              fontSize: 13, fontWeight: 700,
            }}>Salva operatore</div>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Servono tre cose: <Strong>Nome</Strong>, <Strong>Cognome</Strong> e un{' '}
        <Strong>PIN</Strong>. L'interruttore <Strong>Amministratore</Strong> si accende
        solo per chi deve gestire — organico, oggetti, scadenziario, impostazioni: per chi
        deve <em>lavorare</em> (registrare, verificare) non serve, e tenerlo spento è la
        scelta giusta.
      </P>

      {/* o2 */}
      <H id="o2" cat="formazione">2. Il PIN</H>

      <P>
        Il PIN è la chiave personale: all'ingresso l'azienda è già quella del vostro
        indirizzo, e <Strong>il PIN da solo dice chi sei</Strong>. Da questo discendono le
        due regole che contano.
      </P>

      <Passo n="1" titolo="Uno a testa, mai condiviso">
        <P style={{ margin: '4px 0' }}>
          Proprio perché il PIN identifica la persona, due persone della stessa azienda{' '}
          <Strong>non devono avere lo stesso PIN</Strong>: le firme finirebbero sul nome
          sbagliato. E un PIN scritto sul frigorifero è una firma che chiunque può usare —
          il registro vale quanto la segretezza dei PIN che lo firmano.
        </P>
      </Passo>

      <Passo n="2" titolo="Non si recupera: si reimposta">
        <P style={{ margin: '4px 0' }}>
          Il PIN non è leggibile da nessuna parte, nemmeno per un amministratore — è
          cifrato. Chi lo dimentica non lo «recupera»: un amministratore apre la sua
          scheda e ne scrive uno nuovo nel campo{' '}
          <Strong>Nuovo PIN (lascia vuoto per non modificare)</Strong>. Trenta secondi.
        </P>
      </Passo>

      <Callout kind="info" titolo="Troppi tentativi sbagliati">
        Dopo una serie di PIN sbagliati dallo stesso posto, l'ingresso si blocca da solo
        per un po': è la protezione contro chi prova i numeri a caso. Di norma basta
        aspettare qualche minuto e riprovare con calma — e un ingresso riuscito ripulisce
        il contatore.
      </Callout>

      {/* o3 */}
      <H id="o3" cat="produzione">3. La firma grafica</H>

      <P>
        Nella scheda di ogni persona c'è la card <Strong>Firma operatore</Strong>: si
        carica un'immagine della firma (JPG o PNG, fino a 2 MB) e da quel momento compare{' '}
        <Strong>accanto a ogni sua riga nel Registro monitoraggi stampato</Strong>, e nei
        blocchi firma dei verificatori. È il dettaglio che trasforma la stampa da tabulato
        a registro firmato.
      </P>

      <Callout kind="tip" titolo="Come farla venire bene">
        Firma su un foglio bianco con un tratto deciso, foto dall'alto con buona luce, e
        ritagliate solo la firma. Su un nuovo operatore il caricamento è spento finché non
        salvate — <em>«Salva prima l'operatore per poter caricare la firma»</em> — quindi
        l'ordine è: prima la scheda, poi la firma.
      </Callout>

      {/* o4 */}
      <H id="o4" cat="formazione">4. Ruoli veri e ruoli derivati</H>

      <P>
        Nell'elenco, accanto ai nomi, ci sono i contrassegni:{' '}
        <ChipFinto tono="blu">Admin</ChipFinto> per gli amministratori,{' '}
        <ChipFinto tono="viola">Master</ChipFinto> per il fornitore del sistema,{' '}
        <ChipFinto tono="rosso">Disattivato</ChipFinto> per chi non ha più accesso. Questi
        sono i ruoli <Strong>veri</Strong>, quelli che si impostano qui.
      </P>
      <P>
        «Monitoratore» e «verificatore», invece, <Strong>qui non esistono</Strong>: non
        c'è nessuna casella da spuntare. Si è monitoratori perché lo scadenziario assegna
        almeno un'attività, e verificatori perché almeno una voce nomina la persona nel
        campo Verifica — e le voci di menu si accendono da sole, al primo ingresso dopo
        l'assegnazione. Se una persona nuova vede il menu spento, la risposta non è in
        questa schermata: è nello <Code>Scadenziario</Code>.
      </P>

      <Callout kind="warn" titolo="Cosa può fare un amministratore, e cosa no">
        Un amministratore gestisce le persone <Strong>della propria azienda</Strong>:
        crearle, reimpostare i PIN, disattivarle. Non può spostarle in un'altra azienda né
        concedere il grado Master — quello è riservato al fornitore. Il server applica
        questi limiti anche a chi provasse per vie traverse.
      </Callout>

      {/* o5 */}
      <H id="o5" cat="pulizie">5. Quando una persona lascia</H>

      <P>
        La tentazione è «cancellarla». La strada giusta è un'altra: aprire la scheda e
        spegnere l'interruttore, che passa da <Strong>Operatore attivo — può accedere e
        registrare attività</Strong> a <Strong>Operatore disattivato — non può più
        accedere al sistema</Strong>.
      </P>

      <Passo n="1" titolo="Perché disattivare e non cancellare">
        <P style={{ margin: '4px 0' }}>
          Le firme di quella persona restano nel registro degli anni passati, ed è
          esattamente ciò che deve succedere: un registro con firme che spariscono non è
          più un registro. Disattivato, l'operatore non entra più ma la sua storia resta
          intatta e stampabile.
        </P>
      </Passo>

      <Passo n="2" titolo="Il giro completo delle consegne">
        <P style={{ margin: '4px 0' }}>
          Insieme alla disattivazione, passate dallo <Code>Scadenziario</Code>: le
          attività di cui era titolare vanno riassegnate, e va tolto dai supplenti e dai
          verificatori dove compare. Altrimenti restano controlli che nessuno vede
          scadere.
        </P>
      </Passo>

      {/* o6 */}
      <H id="o6">Le trappole</H>

      <Callout kind="warn" titolo="Il nuovo assunto che «non vede niente»">
        Scheda creata, PIN consegnato, ma le voci Attività e Verifiche sono grigie. Non
        manca nessun permesso: manca <Strong>lo scadenziario</Strong> — finché non gli
        viene assegnata almeno un'attività, per il sistema è una persona senza compiti. E
        se l'assegnazione arriva mentre è già dentro, deve uscire e rientrare col PIN.
      </Callout>

      <Callout kind="warn" titolo="Il PIN «di reparto»">
        Un solo PIN usato da tutto il turno fa comodo per una settimana e inquina il
        registro per sempre: tutte le firme risultano della stessa persona, e il giorno
        della non conformità nessuno sa più chi ha fatto cosa. Una scheda a testa, un PIN
        a testa — è il costo di un minuto per persona.
      </Callout>

      <Callout kind="warn" titolo="Disattivare non è riassegnare">
        Spegnere l'accesso non libera i compiti: le attività a scadenziario restano
        intestate alla persona disattivata e vanno spostate a mano. È il secondo passo
        del capitolo 5, ed è quello che si dimentica.
      </Callout>

      {/* o7 */}
      <H id="o7">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Posso vedere il PIN di un operatore?">
          No, nessuno può: è cifrato, e il campo nella scheda serve solo a scriverne uno
          nuovo. Se serve, si reimposta — la persona lo cambierà di nuovo se preferisce
          sceglierselo.
        </FAQ>

        <FAQ q="Quanti amministratori conviene avere?">
          Almeno due — se l'unico amministratore dimentica il PIN, nessuno può
          reimpostarglielo dall'interno. Ma pochi: ogni amministratore può toccare
          scadenziario e organico, e meno mani ci sono, più il sistema resta ordinato.
        </FAQ>

        <FAQ q="Ho disattivato una persona per sbaglio: ho perso qualcosa?">
          No. La disattivazione è reversibile: si riapre la scheda e si riaccende
          l'interruttore. Firme, registrazioni e storia non si toccano mai.
        </FAQ>

        <FAQ q="Un operatore può cambiarsi il PIN da solo?">
          No: il PIN si imposta solo da questa schermata, quindi serve un amministratore.
          È una scelta: il PIN è una firma, e chi lo cambia deve essere tracciabile.
        </FAQ>

        <FAQ q="La firma grafica è obbligatoria?">
          No, il registro è valido anche senza. Ma sulle stampe la differenza si vede — e
          caricarla una volta costa meno che spiegare a ogni ispezione perché accanto a
          quelle righe non c'è.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Oggetti da monitorare</Strong>: le celle, le attrezzature e gli
            ambienti su cui le persone appena inserite andranno a lavorare.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
